<script setup lang="ts">
import type { SubscriberBlockResponse } from '~/types/storyblok/subscriber-block'
import { normalizeSubscriberBlock } from '~/types/storyblok/subscriber-block'

interface Props {
  blok: SubscriberBlockResponse
}

const props = defineProps<Props>()
const blok = toRef(props, 'blok')
const data = computed(() => normalizeSubscriberBlock(blok.value))
const legal = useRichtext(computed(() => blok.value.legal))
const successMessage = useRichtext(computed(() => data.value.successMessage))
const errorMessage = useRichtext(computed(() => data.value.errorMessage))

const formRequeststate = reactive({
  success: false,
  error: false,
})
const bgColor = computed(() => data.value.backgroundColor)
const { bgInvertedColor } = useBackgroundInvertedColor(bgColor.value)

function handleSuccess() {
  formRequeststate.success = true
  useSegment().setSubscribed(data.value.segmentId)
}

const extraFieldsData = computed(() => {
  const extraFields: Record<any, string> = {}

  if (props.blok.segment_id) {
    extraFields.segment_id = props.blok.segment_id
  }

  return extraFields
})
</script>

<template>
  <div
    v-if="blok"
    class="subscriber"
    :style="{ backgroundColor: bgColor }"
  >
    <h2
      v-if="data.title && !formRequeststate.success"
      class="title"
    >
      {{ data.title }}
    </h2>
    <p
      v-if="data.description && !formRequeststate.success"
      class="description"
    >
      {{ data.description }}
    </p>
    <FormForm
      v-if="data.form && !formRequeststate.success"
      :fields="data.form"
      :legal="legal"
      :label="data.ctaLabel"
      :bg-color="bgColor"
      signup-source="page"
      :identifier="data.identifier"
      :extra-data="extraFieldsData"
      @error="formRequeststate.error = true"
      @success="handleSuccess"
    />
    <div role="status">
      <div
        v-if="formRequeststate.error"
        v-html="errorMessage"
      />
      <div
        v-if="formRequeststate.success"
        class="success"
        v-html="successMessage"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/typography/headings';
@import 'assets/scss/typography/body';

.subscriber {
  padding: 2.8rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: v-bind(bgInvertedColor);

  @media (min-width: $large-breakpoint) {
    padding: 3.2rem 4rem 4rem;
  }
}

.title {
  margin-bottom: 1.8rem;
  @include h5;
  color: v-bind(bgInvertedColor);
}

.description {
  margin-bottom: 1.8rem;
  @include body2;
  color: v-bind(bgInvertedColor);
}

.success {
  @include h5;
  align-self: center;
  color: v-bind(bgInvertedColor);
}
</style>
